import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { ConditionalRender } from './ProtectedRoute';

// User interface
interface User {
  firstName?: string;
  lastName?: string;
  username?: string;
  email?: string;
  roles?: string[];
}

// User Avatar Component
const UserAvatar: React.FC<{
  user: User | null;
  size?: 'sm' | 'md' | 'lg';
}> = ({ user, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-6 h-6 text-xs',
    md: 'w-8 h-8 text-sm',
    lg: 'w-12 h-12 text-base',
  };

  const initials =
    user?.firstName && user?.lastName
      ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`
      : user?.username?.charAt(0).toUpperCase() || 'U';

  return (
    <div
      className={`${sizeClasses[size]} rounded-full flex items-center justify-center font-medium`}
      style={{
        backgroundColor: 'var(--accent)',
        color: 'var(--bg-primary)',
      }}
    >
      {initials}
    </div>
  );
};

// User Menu Dropdown
const UserMenu: React.FC = () => {
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 p-2 rounded-lg transition-colors"
        style={{
          color: 'var(--text-primary)',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <UserAvatar user={user} />
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Menu */}
          <div
            className="absolute right-0 mt-2 w-64 rounded-lg shadow-lg border z-20"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              borderColor: 'var(--border)',
              boxShadow: '0 10px 25px var(--shadow)',
            }}
          >
            <div
              className="p-4 border-b"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="items-center space-x-3">
                <div className="justify-center flex mb-2">
                  <UserAvatar user={user} size="lg" />
                </div>
                <div>
                  <div
                    className="font-medium"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {user?.firstName} {user?.lastName}
                  </div>
                  <div
                    className="text-sm"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {user?.email}
                  </div>
                  <div
                    className="text-xs"
                    style={{ color: 'var(--text-tertiary)' }}
                  >
                    @{user?.username}
                  </div>
                </div>
              </div>

              {/* User Roles */}
              {user?.roles && user.roles.length > 0 && (
                <div className="mt-3">
                  <div
                    className="text-xs font-medium mb-1"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    Roles:
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {user.roles.map(role => (
                      <span
                        key={role}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: 'var(--accent)',
                          color: 'var(--bg-primary)',
                        }}
                      >
                        {role}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-2">
              <button
                onClick={() => {
                  setIsOpen(false);
                  // Add profile navigation here if needed
                }}
                className="w-full text-left px-3 py-2 text-sm rounded-md transition-colors"
                style={{ color: 'var(--text-primary)' }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                View Profile
              </button>
              <button
                onClick={() => {
                  setIsOpen(false);
                  // Add settings navigation here if needed
                }}
                className="w-full text-left px-3 py-2 text-sm rounded-md transition-colors"
                style={{ color: 'var(--text-primary)' }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                Settings
              </button>
              <hr className="my-2" style={{ borderColor: 'var(--border)' }} />
              <button
                onClick={() => {
                  setIsOpen(false);
                  logout();
                }}
                className="w-full text-left px-3 py-2 text-sm rounded-md transition-colors"
                style={{ color: '#ef4444' }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)';
                  e.currentTarget.style.color = '#dc2626';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#ef4444';
                }}
              >
                Sign Out
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Authentication Button
const AuthButtons: React.FC = () => {
  const { login } = useAuth();

  return (
    <button
      onClick={() => login()}
      className="px-4 py-2 rounded-lg transition-colors"
      style={{
        backgroundColor: 'var(--accent)',
        color: 'var(--bg-primary)',
        border: '1px solid var(--accent)',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.backgroundColor = 'var(--accent-hover)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.backgroundColor = 'var(--accent)';
      }}
    >
      Sign In
    </button>
  );
};

// Main Authentication Component
export const AuthComponent: React.FC = () => {
  const { isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center space-x-2">
        <div
          className="animate-spin rounded-full h-6 w-6 border-b-2"
          style={{ borderColor: 'var(--accent)' }}
        ></div>
        <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          Loading...
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center">
      <ConditionalRender requireAuth={true} fallback={<AuthButtons />}>
        <UserMenu />
      </ConditionalRender>
    </div>
  );
};

// Conditional content components for easy use in templates
export const AuthenticatedContent: React.FC<{
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ children, fallback = null }) => (
  <ConditionalRender requireAuth={true} fallback={fallback}>
    {children}
  </ConditionalRender>
);

export const UnauthenticatedContent: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => (
  <ConditionalRender requireAuth={false} fallback={children}>
    {null}
  </ConditionalRender>
);

export const RoleBasedContent: React.FC<{
  children: React.ReactNode;
  roles: string[];
  requireAllRoles?: boolean;
  fallback?: React.ReactNode;
}> = ({ children, roles, requireAllRoles = false, fallback = null }) => (
  <ConditionalRender
    roles={roles}
    requireAllRoles={requireAllRoles}
    fallback={fallback}
  >
    {children}
  </ConditionalRender>
);

export default AuthComponent;
